import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  Tabs,
  Tab,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ViewKanban as ViewKanbanIcon,
  ViewList as ViewListIcon,
  FilterList as FilterListIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { supabase } from '../services/supabaseClient';
import { usePermissions } from '../hooks/usePermissions';
import { Project } from '../services/mockProjects';
import { useTheme, useMediaQuery } from '@mui/material';

const Projects: React.FC = () => {
  const { user, session } = useAuth();
  const navigate = useNavigate();
  const { hasPermission, isAdmin, isAgence } = usePermissions();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [projects, setProjects] = useState<Project[]>([]);
  const [filteredProjects, setFilteredProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'kanban'>('list');
  const [filters, setFilters] = useState({
    status: 'all',
    priority: 'all',
    zone: 'all',
    search: '',
  });
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    objectives: '',
    priority: 'medium',
    assigned_to: '',
    manager_agence: '',
    contact_soebra: '',
    start_date: '',
    due_date: '',
    budget: '',
    zones: [] as string[],
  });

  useEffect(() => {
    // Attendre que la session soit disponible avant de charger
    if (session) {
      loadProjects();
    }
  }, [session]);

  useEffect(() => {
    applyFilters();
  }, [filters, projects]);

  const loadProjects = async () => {
    try {
      setLoading(true);
      
      // Vérifier la session avant de charger
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        console.warn('Aucune session active, impossible de charger les projets');
        setProjects([]);
        setLoading(false);
        return;
      }
      
      const response = await api.projects.getAll();
      // Transformer les données Supabase en format Project
      const transformedProjects: Project[] = (response.projects || []).map((p: any) => ({
        id: p.id,
        title: p.title,
        description: p.description || '',
        objectives: p.objectives || '',
        status: p.status,
        priority: p.priority,
        created_by: p.created_by,
        created_by_name: p.created_by_user?.name || '',
        manager_agence: p.manager_agence,
        manager_agence_name: p.manager_agence_user?.name || '',
        contact_soebra: p.contact_soebra,
        contact_soebra_name: p.contact_soebra_user?.name || '',
        assigned_to: p.assigned_to,
        assigned_to_name: p.assigned_to_user?.name || '',
        start_date: p.start_date ? new Date(p.start_date).toISOString().split('T')[0] : undefined,
        due_date: p.due_date ? new Date(p.due_date).toISOString() : undefined,
        budget: p.budget ? parseFloat(p.budget) : undefined,
        zones: p.zones || [],
        kpi_objectives: p.kpi_objectives || [],
        created_at: p.created_at,
        updated_at: p.updated_at || p.created_at,
        location: p.location,
        post_mortem: p.post_mortem,
        is_archived: p.is_archived || false,
      }));
      setProjects(transformedProjects);
    } catch (error) {
      console.error('Erreur lors du chargement des projets:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...projects];

    if (filters.status !== 'all') {
      filtered = filtered.filter(p => p.status === filters.status);
    }

    if (filters.priority !== 'all') {
      filtered = filtered.filter(p => p.priority === filters.priority);
    }

    if (filters.zone !== 'all') {
      filtered = filtered.filter(p => p.zones?.includes(filters.zone));
    }

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(
        p =>
          p.title.toLowerCase().includes(searchLower) ||
          p.description.toLowerCase().includes(searchLower)
      );
    }

    setFilteredProjects(filtered);
  };

  const handleCreateProject = async () => {
    try {
      if (!formData.title) {
        alert('Le titre est requis');
        return;
      }

      console.log('=== DÉBUT CRÉATION PROJET ===');
      console.log('Données du formulaire:', formData);
      console.log('Utilisateur connecté:', user);

      const projectData: any = {
        title: formData.title,
        description: formData.description || null,
        priority: formData.priority,
        assigned_to: formData.assigned_to || null,
        due_date: formData.due_date ? new Date(formData.due_date).toISOString() : null,
        objectives: formData.objectives || null,
        manager_agence: formData.manager_agence || null,
        contact_soebra: formData.contact_soebra || null,
        start_date: formData.start_date ? new Date(formData.start_date).toISOString() : null,
        budget: formData.budget ? parseFloat(formData.budget) : null,
        zones: formData.zones.length > 0 ? formData.zones : null,
      };

      console.log('Données envoyées à l\'API:', projectData);
      
      const response = await api.projects.create(projectData);
      
      console.log('Réponse de l\'API:', response);
      
      // Recharger les projets depuis Supabase
      await loadProjects();
      
      setOpenDialog(false);
      setFormData({
        title: '',
        description: '',
        objectives: '',
        priority: 'medium',
        assigned_to: '',
        manager_agence: '',
        contact_soebra: '',
        start_date: '',
        due_date: '',
        budget: '',
        zones: [],
      });
      
      console.log('=== PROJET CRÉÉ AVEC SUCCÈS ===');
      alert('Projet créé avec succès !');
    } catch (error: any) {
      console.error('=== ERREUR LORS DE LA CRÉATION ===');
      console.error('Type d\'erreur:', typeof error);
      console.error('Message:', error?.message);
      console.error('Code:', error?.code);
      console.error('Details:', error?.details);
      console.error('Erreur complète:', error);
      alert('Erreur lors de la création du projet:\n' + 
            (error?.message || 'Erreur inconnue') + '\n' +
            (error?.details ? 'Détails: ' + error.details : '') +
            '\n\nVérifiez la console pour plus de détails.');
    }
  };

  const handleDeleteProject = async (projectId: string) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce projet ?')) {
      return;
    }

    try {
      await api.projects.delete(projectId);
      await loadProjects();
    } catch (error: any) {
      console.error('Erreur lors de la suppression du projet:', error);
      alert('Erreur lors de la suppression: ' + (error.message || 'Erreur inconnue'));
    }
  };

  const getStatusLabel = (status: string): string => {
    const statusMap: Record<string, string> = {
      'pending': 'En Attente',
      'in_progress': 'En Cours',
      'review': 'En Revue SOBEBRA',
      'completed': 'Terminé',
      'cancelled': 'Annulé',
      'archived': 'Archivé',
    };
    return statusMap[status] || status;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'primary';
      case 'review':
        return 'warning';
      case 'cancelled':
        return 'error';
      case 'archived':
        return 'default';
      default:
        return 'default';
    }
  };

  const getPriorityLabel = (priority: string): string => {
    const priorityMap: Record<string, string> = {
      'low': 'Basse',
      'medium': 'Moyenne',
      'high': 'Haute',
      'urgent': 'Urgente',
    };
    return priorityMap[priority] || priority;
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'error';
      case 'high':
        return 'warning';
      case 'medium':
        return 'info';
      default:
        return 'default';
    }
  };

  // Calcul des statistiques
  const stats = {
    total: projects.length,
    active: projects.filter(p => p.status === 'in_progress').length,
    completed: projects.filter(p => p.status === 'completed').length,
    overdue: projects.filter(p => {
      if (!p.due_date) return false;
      const dueDate = new Date(p.due_date);
      const today = new Date();
      const diffDays = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      return diffDays <= 7 && diffDays >= 0 && p.status !== 'completed';
    }).length,
  };

  // Projets en retard
  const overdueProjects = projects.filter(p => {
    if (!p.due_date) return false;
    const dueDate = new Date(p.due_date);
    const today = new Date();
    const diffDays = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return diffDays <= 7 && diffDays >= 0 && p.status !== 'completed';
  });

  // Vue Kanban
  const kanbanColumns = [
    { id: 'pending', label: 'En Attente', statuses: ['pending'] },
    { id: 'in_progress', label: 'En Cours', statuses: ['in_progress'] },
    { id: 'review', label: 'En Revue SOBEBRA', statuses: ['review'] },
    { id: 'completed', label: 'Clôturé', statuses: ['completed', 'archived'] },
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Dashboard Synthétique */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total Projets
                  </Typography>
                  <Typography variant="h4">{stats.total}</Typography>
                </Box>
                <TrendingUpIcon sx={{ fontSize: 40, color: '#1976d2', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Actifs
                  </Typography>
                  <Typography variant="h4" color="primary">{stats.active}</Typography>
                </Box>
                <CheckCircleIcon sx={{ fontSize: 40, color: '#2e7d32', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Terminés
                  </Typography>
                  <Typography variant="h4" color="success.main">{stats.completed}</Typography>
                </Box>
                <CheckCircleIcon sx={{ fontSize: 40, color: '#2e7d32', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    En Retard (7j)
                  </Typography>
                  <Typography variant="h4" color="error">{stats.overdue}</Typography>
                </Box>
                <WarningIcon sx={{ fontSize: 40, color: '#f44336', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Alertes Rapides */}
      {overdueProjects.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            ⚠️ {overdueProjects.length} projet(s) approchant de leur date limite :
          </Typography>
          {overdueProjects.map(p => (
            <Typography key={p.id} variant="body2">
              • {p.title} - Échéance: {new Date(p.due_date!).toLocaleDateString('fr-FR')}
            </Typography>
          ))}
        </Alert>
      )}

      {/* En-tête avec filtres et actions */}
      <Box 
        display="flex" 
        justifyContent="space-between" 
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        flexDirection={{ xs: 'column', sm: 'row' }}
        gap={{ xs: 2, sm: 0 }}
        mb={3}
      >
        <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>Projets</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Tooltip title={viewMode === 'list' ? 'Vue Kanban' : 'Vue Liste'}>
            <IconButton onClick={() => setViewMode(viewMode === 'list' ? 'kanban' : 'list')}>
              {viewMode === 'list' ? <ViewKanbanIcon /> : <ViewListIcon />}
            </IconButton>
          </Tooltip>
          {hasPermission('projects:create') && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenDialog(true)}
              size={isMobile ? 'small' : 'medium'}
              fullWidth={isMobile}
            >
              Nouveau projet
            </Button>
          )}
        </Box>
      </Box>

      {/* Filtres Avancés */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Rechercher"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              InputProps={{
                startAdornment: <FilterListIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Statut</InputLabel>
              <Select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <MenuItem value="all">Tous</MenuItem>
                <MenuItem value="pending">En Attente</MenuItem>
                <MenuItem value="in_progress">En Cours</MenuItem>
                <MenuItem value="review">En Revue SOBEBRA</MenuItem>
                <MenuItem value="completed">Terminé</MenuItem>
                <MenuItem value="cancelled">Annulé</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Priorité</InputLabel>
              <Select
                value={filters.priority}
                onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
              >
                <MenuItem value="all">Toutes</MenuItem>
                <MenuItem value="urgent">Urgente</MenuItem>
                <MenuItem value="high">Haute</MenuItem>
                <MenuItem value="medium">Moyenne</MenuItem>
                <MenuItem value="low">Basse</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Zone</InputLabel>
              <Select
                value={filters.zone}
                onChange={(e) => setFilters({ ...filters, zone: e.target.value })}
              >
                <MenuItem value="all">Toutes</MenuItem>
                <MenuItem value="Dantokpa">Dantokpa</MenuItem>
                <MenuItem value="Ganhi">Ganhi</MenuItem>
                <MenuItem value="Fidjrossè">Fidjrossè</MenuItem>
                <MenuItem value="Akpakpa">Akpakpa</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Vue Liste */}
      {viewMode === 'list' && (
        <TableContainer 
          component={Paper}
          sx={{
            overflowX: 'auto',
            '& .MuiTable-root': {
              minWidth: 650,
            }
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Titre</TableCell>
                <TableCell>Statut</TableCell>
                <TableCell>Priorité</TableCell>
                <TableCell>Manager</TableCell>
                <TableCell>Assigné à</TableCell>
                <TableCell>Date limite</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredProjects.length === 0 ? (
                <TableRow>
                  {/* @ts-ignore - colSpan is valid HTML attribute */}
                  <TableCell colSpan={7} align="center">
                    Aucun projet trouvé
                  </TableCell>
                </TableRow>
              ) : (
                filteredProjects.map((project) => (
                  <TableRow key={project.id} hover>
                    <TableCell>
                      <Typography
                        variant="body1"
                        sx={{ cursor: 'pointer', fontWeight: 'medium' }}
                        onClick={() => navigate(`/projects/${project.id}`)}
                      >
                        {project.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {project.location || 'Non spécifié'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(project.status)}
                        size="small"
                        color={getStatusColor(project.status) as any}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getPriorityLabel(project.priority)}
                        size="small"
                        color={getPriorityColor(project.priority) as any}
                      />
                    </TableCell>
                    <TableCell>{project.manager_agence_name || '-'}</TableCell>
                    <TableCell>{project.assigned_to_name || 'Non assigné'}</TableCell>
                    <TableCell>
                      {project.due_date
                        ? new Date(project.due_date).toLocaleDateString('fr-FR')
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Modifier">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/projects/${project.id}`)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        {(isAdmin || isAgence) && (
                          <Tooltip title="Supprimer">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteProject(project.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Vue Kanban */}
      {viewMode === 'kanban' && (
        <Grid container spacing={2}>
          {kanbanColumns.map((column) => {
            const columnProjects = filteredProjects.filter(p =>
              column.statuses.includes(p.status)
            );
            return (
              <Grid item xs={12} sm={6} md={3} key={column.id}>
                <Paper sx={{ p: 2, minHeight: 500 }}>
                  <Typography variant="h6" gutterBottom fontWeight="bold">
                    {column.label === 'En Attente' ? 'En Attente' :
                     column.label === 'En Cours' ? 'En Cours' :
                     column.label === 'En Revue SOBEBRA' ? 'En Revue SOBEBRA' :
                     column.label === 'Clôturé' ? 'Clôturé' : column.label} ({columnProjects.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                    {columnProjects.map((project) => (
                      <Paper
                        key={project.id}
                        elevation={2}
                        sx={{
                          p: 2,
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                        onClick={() => navigate(`/projects/${project.id}`)}
                      >
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                          {project.title}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
                          <Chip
                            label={getPriorityLabel(project.priority)}
                            size="small"
                            color={getPriorityColor(project.priority) as any}
                          />
                        </Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {project.assigned_to_name || 'Non assigné'}
                        </Typography>
                        {project.due_date && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Échéance: {new Date(project.due_date).toLocaleDateString('fr-FR')}
                          </Typography>
                        )}
                      </Paper>
                    ))}
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Dialog Création Projet */}
      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Créer un nouveau projet</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Titre"
            margin="normal"
            required
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          />
          <TextField
            fullWidth
            label="Description"
            margin="normal"
            multiline
            rows={3}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />
          <TextField
            fullWidth
            label="Objectifs"
            margin="normal"
            multiline
            rows={2}
            value={formData.objectives}
            onChange={(e) => setFormData({ ...formData, objectives: e.target.value })}
            helperText="Objectifs mesurables du projet"
          />
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Priorité"
                margin="normal"
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
              >
                <MenuItem value="low">Basse</MenuItem>
                <MenuItem value="medium">Moyenne</MenuItem>
                <MenuItem value="high">Haute</MenuItem>
                <MenuItem value="urgent">Urgente</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date de début"
                margin="normal"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date limite"
                margin="normal"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={formData.due_date}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
              />
            </Grid>
            {(isAdmin || isAgence) && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Budget Estimé (FCFA)"
                  margin="normal"
                  type="number"
                  value={formData.budget}
                  onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Annuler</Button>
          <Button onClick={handleCreateProject} variant="contained" disabled={!formData.title}>
            Créer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Projects;
